import React, { useState, useEffect } from "react";
import { Link } from "@quintype/components";
import "./mobile-header.m.css";

export default function MobileNavItem({ menu, currentPath, allMenus, currentIndex }) {
  // State to track if we're on the client (hydrated)
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Helper to extract path from URL (handles both full URLs and relative paths)
  const extractPath = (url) => {
    if (!url) return '';
    try {
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return urlObj.pathname;
      }
      return url;
    } catch (e) {
      return url;
    }
  };

  // Check if current path matches this menu or its children
  const checkMatch = (m, path) => {
    if (!path) return false;
    const cleanPath = path.split('?')[0].split('#')[0];

    // Direct match or sub-path match
    const menuPath = extractPath(m.completeUrl);
    if (menuPath === cleanPath) return true;
    if (cleanPath.startsWith(menuPath + '/')) return true;

    if (m.children && m.children.length > 0) {
      for (const child of m.children) {
        // Skip placeholder and link items
        const itemType = child['item-type'] || child.itemType;
        if (itemType === 'placeholder' || itemType === 'link') {
          continue;
        }

        const childUrl = child.completeUrl || child.url;
        const childPath = extractPath(childUrl);
        if (childPath === cleanPath) return true;
        if (cleanPath.startsWith(childPath + '/')) return true;

        if (child.children && child.children.length > 0) {
          for (const grandchild of child.children) {
            // Skip placeholder and link items
            const grandchildItemType = grandchild['item-type'] || grandchild.itemType;
            if (grandchildItemType === 'placeholder' || grandchildItemType === 'link') {
              continue;
            }

            const grandchildUrl = grandchild.completeUrl || grandchild.url;
            const grandchildPath = extractPath(grandchildUrl);
            if (grandchildPath === cleanPath) return true;
            if (cleanPath.startsWith(grandchildPath + '/')) return true;
          }
        }
      }
    }
    return false;
  };

  // Determine if this menu should be highlighted
  const active = (() => {
    if (!isClient) return false;
    if (!currentPath) return false;

    // Clean the current path for comparison (remove query strings and hash)
    const cleanPath = currentPath.split('?')[0].split('#')[0];

    // FIRST: Check if a secondary menu was clicked - if so, NEVER highlight primary menus
    if (isClient) {
      try {
        const clicked = sessionStorage.getItem('clickedMenu');
        if (clicked) {
          const { url, type } = JSON.parse(clicked);
          // If clicked from secondary menu and current path matches, don't highlight any primary menu
          if (type === 'secondary' && url === cleanPath) {
            return false;
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Check if this menu matches the current path
    const matches = checkMatch(menu, currentPath);
    if (!matches) return false;

    // Check if user clicked from this specific primary menu
    if (isClient) {
      try {
        const clicked = sessionStorage.getItem('clickedMenu');
        if (clicked) {
          const { url, menuTitle, type, index } = JSON.parse(clicked);
          // If clicked from primary and URL matches, highlight if index matches (preferred) or title matches
          if (type === 'primary' && url === cleanPath) {
            if (index !== undefined) {
              return currentIndex === index;
            }
            return menu.title === menuTitle;
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }

    // Default: only highlight if this is the first matching menu
    if (allMenus && currentIndex !== undefined) {
      for (let i = 0; i < currentIndex; i++) {
        if (checkMatch(allMenus[i], currentPath)) {
          return false;
        }
      }
    }

    return true;
  })();

  // Store which parent menu was clicked
  const handleMenuClick = () => {
    if (!isClient) return;
    try {
      const urlPath = extractPath(menu.completeUrl);
      const cleanUrl = urlPath.split('?')[0].split('#')[0];
      sessionStorage.setItem('clickedMenu', JSON.stringify({
        url: cleanUrl,
        menuTitle: menu.title,
        type: 'primary',
        index: currentIndex
      }));
    } catch (e) {
      // Ignore storage errors
    }
  };

  return (
    <Link
      href={menu.completeUrl}
      styleName={active ? "menu active" : "menu"}
      onMouseDown={handleMenuClick}
    >
      {menu.title}
    </Link>
  );
}
